import React, { useMemo, useState } from 'react';
import { AVAILABLE_MODULES } from '../config/modules';

// Liberação de módulos em duas listas (como o "Usuário x Perfil" de outros sistemas):
// à esquerda o que NÃO está liberado, à direita o que está — e, em cada liberado,
// se a pessoa só vê ou também edita. Cada mudança chama onChange(permissõesNovas).

const C = {
  painel: { flex: '1 1 320px', minWidth: 0, border: '1px solid #e5e7eb', borderRadius: 10, display: 'flex', flexDirection: 'column', background: '#fff' },
  cab: { padding: '10px 12px', borderBottom: '1px solid #eef0f4', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 },
  titulo: { fontWeight: 700, fontSize: '0.88rem', color: '#1e293b' },
  conta: { fontSize: '0.72rem', fontWeight: 700, color: '#64748b', background: '#f1f5f9', borderRadius: 10, padding: '2px 8px' },
  lista: { overflowY: 'auto', maxHeight: 380, flex: 1 },
  grupo: { padding: '6px 12px', background: '#eef2ff', color: '#4f46e5', fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', display: 'flex', alignItems: 'center', gap: 8, position: 'sticky', top: 0, zIndex: 1 },
  linha: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', borderTop: '1px solid #f5f6f8', fontSize: '0.85rem', color: '#334155', cursor: 'pointer', userSelect: 'none' },
  rodape: { padding: '8px 12px', borderTop: '1px solid #eef0f4', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' },
  btn: (cor, ativo) => ({ border: `1px solid ${ativo ? cor : '#e2e8f0'}`, background: ativo ? cor : '#fff', color: ativo ? '#fff' : '#94a3b8', borderRadius: 7, padding: '6px 12px', fontSize: '0.8rem', fontWeight: 700, cursor: ativo ? 'pointer' : 'default' }),
  busca: { width: '100%', boxSizing: 'border-box', padding: '7px 10px', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: '0.84rem', outline: 'none' },
  vazio: { padding: '28px 12px', textAlign: 'center', color: '#94a3b8', fontSize: '0.84rem' },
};

const semAcento = s => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

function NivelAcesso({ edit, onChange, disabled }) {
  const opc = (valor, rotulo) => (
    <button
      type="button" disabled={disabled}
      onClick={e => { e.stopPropagation(); if (edit !== valor) onChange(valor); }}
      style={{
        border: 'none', padding: '3px 9px', fontSize: '0.74rem', fontWeight: 700, cursor: disabled ? 'default' : 'pointer',
        background: edit === valor ? (valor ? '#16a34a' : '#2563eb') : 'transparent',
        color: edit === valor ? '#fff' : '#64748b',
      }}
    >{rotulo}</button>
  );
  return (
    <span style={{ display: 'inline-flex', border: '1px solid #e2e8f0', borderRadius: 7, overflow: 'hidden', flexShrink: 0 }}>
      {opc(false, 'Ver')}{opc(true, 'Ver e editar')}
    </span>
  );
}

export default function PermissoesLiberacao({ permissions = {}, onChange, disabled = false }) {
  const [busca, setBusca] = useState('');
  const [selEsq, setSelEsq] = useState(() => new Set());
  const [selDir, setSelDir] = useState(() => new Set());

  const liberado = id => !!(permissions[id]?.view || permissions[id]?.edit);
  const casaBusca = m => !busca.trim() || semAcento(`${m.label} ${m.group}`).includes(semAcento(busca));

  const naoLiberados = useMemo(() => AVAILABLE_MODULES.filter(m => !liberado(m.id) && casaBusca(m)), [permissions, busca]); // eslint-disable-line react-hooks/exhaustive-deps
  const liberados = useMemo(() => AVAILABLE_MODULES.filter(m => liberado(m.id)), [permissions]); // eslint-disable-line react-hooks/exhaustive-deps
  const totalNaoLiberados = AVAILABLE_MODULES.length - liberados.length;

  const aplicar = (ids, valor) => {
    if (disabled || !ids.length) return;
    onChange({ ...permissions, ...Object.fromEntries(ids.map(id => [id, valor])) });
  };
  const liberar = (ids, edit) => { aplicar(ids, { view: true, edit }); setSelEsq(new Set()); };
  const retirar = ids => { aplicar(ids, { view: false, edit: false }); setSelDir(new Set()); };

  const alterna = (set, setSet, id) => {
    const novo = new Set(set);
    if (novo.has(id)) novo.delete(id); else novo.add(id);
    setSet(novo);
  };
  const alternaGrupo = (set, setSet, ids) => {
    const novo = new Set(set);
    const todos = ids.every(id => novo.has(id));
    ids.forEach(id => (todos ? novo.delete(id) : novo.add(id)));
    setSet(novo);
  };

  const porGrupo = mods => {
    const grupos = [];
    mods.forEach(m => {
      let g = grupos.find(x => x.nome === m.group);
      if (!g) { g = { nome: m.group, mods: [] }; grupos.push(g); }
      g.mods.push(m);
    });
    return grupos;
  };

  const rotulo = m => (
    <span style={{ flex: 1, minWidth: 0, color: m.subFunc ? '#7c3aed' : undefined, fontStyle: m.subFunc ? 'italic' : 'normal', fontSize: m.subFunc ? '0.8rem' : undefined }}>{m.label}</span>
  );

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'stretch', opacity: disabled ? 0.6 : 1 }}>
      {/* ── Não liberados ── */}
      <div style={C.painel}>
        <div style={C.cab}>
          <span style={C.titulo}>Não liberados</span>
          <span style={C.conta}>{totalNaoLiberados}</span>
        </div>
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #eef0f4' }}>
          <input style={C.busca} placeholder="🔍 Buscar módulo..." value={busca} onChange={e => setBusca(e.target.value)} disabled={disabled} />
        </div>
        <div style={C.lista}>
          {naoLiberados.length === 0 && <div style={C.vazio}>{busca ? 'Nenhum módulo encontrado.' : 'Tudo liberado.'}</div>}
          {porGrupo(naoLiberados).map(g => {
            const ids = g.mods.map(m => m.id);
            return (
              <div key={g.nome}>
                <label style={C.grupo}>
                  <input type="checkbox" disabled={disabled} checked={ids.every(id => selEsq.has(id))} onChange={() => alternaGrupo(selEsq, setSelEsq, ids)} />
                  {g.nome}
                </label>
                {g.mods.map(m => (
                  <div
                    key={m.id} style={{ ...C.linha, background: selEsq.has(m.id) ? '#eff6ff' : undefined, paddingLeft: m.subFunc ? 28 : 12 }}
                    onClick={() => !disabled && alterna(selEsq, setSelEsq, m.id)}
                    onDoubleClick={() => liberar([m.id], false)}
                    title="Dois cliques: liberar (Ver)"
                  >
                    <input type="checkbox" disabled={disabled} checked={selEsq.has(m.id)} onChange={() => alterna(selEsq, setSelEsq, m.id)} onClick={e => e.stopPropagation()} />
                    {rotulo(m)}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        <div style={C.rodape}>
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{selEsq.size} selecionado(s)</span>
          <span style={{ display: 'flex', gap: 6 }}>
            <button type="button" style={C.btn('#2563eb', selEsq.size > 0 && !disabled)} disabled={!selEsq.size || disabled} onClick={() => liberar([...selEsq], false)}>Liberar — Ver →</button>
            <button type="button" style={C.btn('#16a34a', selEsq.size > 0 && !disabled)} disabled={!selEsq.size || disabled} onClick={() => liberar([...selEsq], true)}>Liberar — Ver e editar →</button>
          </span>
        </div>
      </div>

      {/* ── Liberados ── */}
      <div style={C.painel}>
        <div style={C.cab}>
          <span style={C.titulo}>Liberados para o usuário</span>
          <span style={{ ...C.conta, background: '#dcfce7', color: '#166534' }}>{liberados.length}</span>
        </div>
        <div style={C.lista}>
          {liberados.length === 0 && <div style={C.vazio}>Nenhum módulo liberado.<br />Selecione à esquerda e clique em "Liberar".</div>}
          {porGrupo(liberados).map(g => {
            const ids = g.mods.map(m => m.id);
            return (
              <div key={g.nome}>
                <label style={C.grupo}>
                  <input type="checkbox" disabled={disabled} checked={ids.every(id => selDir.has(id))} onChange={() => alternaGrupo(selDir, setSelDir, ids)} />
                  {g.nome}
                </label>
                {g.mods.map(m => (
                  <div
                    key={m.id} style={{ ...C.linha, background: selDir.has(m.id) ? '#fef2f2' : undefined, paddingLeft: m.subFunc ? 28 : 12 }}
                    onClick={() => !disabled && alterna(selDir, setSelDir, m.id)}
                  >
                    <input type="checkbox" disabled={disabled} checked={selDir.has(m.id)} onChange={() => alterna(selDir, setSelDir, m.id)} onClick={e => e.stopPropagation()} />
                    {rotulo(m)}
                    <NivelAcesso edit={!!permissions[m.id]?.edit} disabled={disabled} onChange={edit => aplicar([m.id], { view: true, edit })} />
                  </div>
                ))}
              </div>
            );
          })}
        </div>
        <div style={C.rodape}>
          <button type="button" style={C.btn('#dc2626', selDir.size > 0 && !disabled)} disabled={!selDir.size || disabled} onClick={() => retirar([...selDir])}>← Retirar selecionados</button>
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{selDir.size} selecionado(s)</span>
        </div>
      </div>
    </div>
  );
}
